import { useCallback, useEffect, useRef, useState } from 'react'
import { apiBaseUrl } from 'constants/apiBaseUrl'
import { getAuthToken } from 'core/api/axiosClient'
import { useAuth } from 'hooks/auth/useAuth'
import type { AssistantChartType } from './assistantToolPrefs'
import {
  createOwnChat,
  deleteOwnChats,
  renameOwnChat,
  mergeTranscriptMessages,
  readActiveChatId,
  saveOwnTranscript,
  sortChatsByRecent,
  subscribeChatMeta,
  subscribeOwnChats,
  subscribeOwnTranscript,
  writeActiveChatId,
  type AssistantChatSummary,
} from './assistantChats'

export type { AssistantChartType } from './assistantToolPrefs'
export type AssistantRole = 'user' | 'assistant'
export type AssistantEffort = 'low' | 'medium' | 'high'

export type AssistantChartData = {
  assistantChart?: true
  needsChartType?: boolean
  allowedTypes?: AssistantChartType[]
  chartType?: AssistantChartType | null
  title: string
  xLabel?: string
  yLabel?: string
  categories?: string[]
  series?: Array<{ label: string; data: number[] }>
  slices?: Array<{ label: string; value: number }>
  note?: string
  error?: string
}

export type AssistantPhotoKind =
  | 'damage_photo'
  | 'photo'
  | 'invoice'
  | 'estimate'
  | 'attachment'
  | 'video'

export type AssistantPhotoItem = {
  storagePath: string
  kind: AssistantPhotoKind
  caption: string
}

export type AssistantImageCardData = {
  imageCard?: true
  title: string
  vehicleLabel?: string | null
  items: AssistantPhotoItem[]
  totalFound?: number
  truncated?: boolean
  path?: string
  note?: string
  error?: string
}

export type InventoryFieldPatch = {
  field: string
  label?: string
  from?: string | number | boolean | null
  to: string | number | boolean | null
}

export type ProposedAction =
  | {
      type: 'createLead'
      payload: {
        customerName?: string
        companyName?: string
        email?: string
        phone?: string
        notes?: string
        priority?: 'low' | 'medium' | 'high'
      }
    }
  | {
      type: 'updateLeadStatus'
      payload: {
        leadId: string
        leadLabel?: string
        fromStatus?: string
        status: string
      }
    }
  | {
      type: 'updateInventoryStatus'
      payload: {
        vehicleId: string
        vehicleLabel?: string
        fromStatus?: string
        status: string
      }
    }
  | {
      type: 'updateInventoryFields'
      payload: {
        vehicleId: string
        vehicleLabel?: string
        fields: InventoryFieldPatch[]
      }
    }
  | {
      type: 'applyGraybookPrice'
      payload: {
        vehicleId: string
        vehicleLabel?: string
        retail: number
        wholesale?: number | null
        field: 'optic_list_price'
        currentListPrice?: number | null
      }
    }
  | { type: 'navigate'; payload: { path: string } }
  | { type: 'clockIn'; payload: Record<string, never> }
  | {
      type: 'createInventoryList'
      payload: {
        name: string
        description: string
        inventoryIds: string[]
        truncated?: boolean
      }
    }
  | {
      type: 'createMarketingCampaign'
      payload: {
        name: string
        subject?: string
        htmlBody?: string
        featuredVehicleIds?: string[]
        status: 'draft'
        audienceFilter?: {
          kind: 'group'
          id: string
          combinator: 'and'
          children: Array<Record<string, unknown>>
        }
      }
    }
  | {
      type: 'sendMarketingCampaign'
      payload: {
        campaignId: string
        name?: string
      }
    }
  | {
      type: 'createRoutine'
      payload: {
        kind: 'email' | 'report'
        reportTopic?: 'sales' | 'inventory' | 'custom' | null
        schedule: 'once' | 'daily' | 'weekdays' | 'weekly' | 'custom'
        times?: string[]
        time?: string
        days?: Array<'Sun' | 'Mon' | 'Tue' | 'Wed' | 'Thu' | 'Fri' | 'Sat'>
        date?: string | null
        recipientUserIds?: string[]
        recipientEmails?: string[]
        prompt: string
      }
    }
  | {
      type: 'runRoutine'
      payload: {
        routineId: string
        choices: Array<{ id: string; label: string }>
      }
    }

export type GraybookCardData = {
  found: boolean
  vehicle?: {
    id: string
    vin: string | null
    stockNumber: string | null
    year: number | null
    make: string | null
    model: string | null
    type: string | null
    size: string | null
    fuel: string | null
    color: string | null
    miles: number | null
    opticListPrice: number | null
    sellerAskingPrice: number | null
    path: string
  }
  method: 'OLS Model' | 'Formula Rules' | null
  matchedFormula?: {
    id: string
    name: string
    wholesaleDiscount: number
    minPrice: number
    maxPrice: number
  }
  retail: number | null
  wholesale: number | null
  breakdown: Array<{ label: string; amount: number }>
  dealQuality?: { label: string; vsWholesalePct: number | null }
  chart?: {
    xLabel: 'mileage'
    mileagePoints: number[]
    series: Array<{
      id: 'formula' | 'ols' | 'current'
      label: string
      data: Array<number | null>
    }>
    currentPoint?: { mileage: number; retail: number; wholesale: number | null }
  }
  reasoning: string[]
  note: string
  path: string
  error?: string
  graybookCard?: true
}

export type AssistantTableData = {
  assistantTable?: true
  title: string
  columns: string[]
  rows: Array<{ cells: string[]; path?: string }>
}

export type AssistantPreviewKind = 'inventory' | 'lead' | 'page' | 'application'

export type AssistantPreviewData = {
  assistantPreview?: true
  kind: AssistantPreviewKind
  title: string
  subtitle?: string
  facts?: Array<{ label: string; value: string }>
  path: string
  imageUrl?: string
}

export type AssistantSuggestion = {
  label: string
  prompt: string
}

export type AssistantToolStep = {
  id: string
  name: string
  phase: 'start' | 'done'
}

export interface AssistantScanNote {
  maxRecords: number
  hitCap: boolean
  startDate: string
  endDate: string
  windowCapped: boolean
  scanCapped: boolean
}

export interface AssistantChatMessage {
  id: string
  role: AssistantRole
  content: string
  /** Set on a shared assistant thread so the other person can see who asked. */
  authorId?: string
  authorName?: string
  /** The reply stopped on an error. Retry sends the question again. */
  failed?: boolean
  proposedActions?: ProposedAction[]
  graybookCards?: GraybookCardData[]
  charts?: AssistantChartData[]
  imageCards?: AssistantImageCardData[]
  tables?: AssistantTableData[]
  previews?: AssistantPreviewData[]
  suggestions?: AssistantSuggestion[]
  scanNotes?: AssistantScanNote[]
}

interface UseAssistantChatOptions {
  currentPath: string
  effort?: AssistantEffort
  /** Picker choice; empty uses the server default. */
  model?: string
  /** Optional tools the user enabled in settings (e.g. graphing). */
  toolPrefs?: {
    graphing?: boolean
    chartTypes?: AssistantChartType[]
    maxRecords?: number
    rangePreset?: 'last_90' | 'this_month' | 'last_month' | 'this_year' | 'custom'
    rangeStart?: string
    rangeEnd?: string
  }
  /** Full-screen Assistant Mode. The API asks for tables, previews, and follow-ups. */
  workspace?: boolean
  /** Named assistant currently open. Personal memory stays off for that thread. */
  assistantId?: string | null
  /** Chat owner written on the transcript. Shared threads keep the creator's id. */
  transcriptOwnerId?: string | null
  /** Name shown on this admin's messages in a shared thread. */
  authorName?: string | null
  /** When false, skip Firestore chat listeners until the assistant UI is open. */
  subscriptionsEnabled?: boolean
}

export function useAssistantChat({
  currentPath,
  effort = 'high',
  model = '',
  toolPrefs,
  workspace = false,
  assistantId = null,
  transcriptOwnerId = null,
  authorName = null,
  subscriptionsEnabled = true,
}: UseAssistantChatOptions) {
  const { currentUser } = useAuth()
  const uid = currentUser?.uid ?? null
  const [messages, setMessages] = useState<AssistantChatMessage[]>([])
  const [threads, setThreads] = useState<AssistantChatSummary[]>([])
  const [chatId, setChatId] = useState<string | null>(() => readActiveChatId())
  const [streamingChatIds, setStreamingChatIds] = useState<string[]>([])
  const [toolStepsByChat, setToolStepsByChat] = useState<Record<string, AssistantToolStep[]>>({})
  const [error, setError] = useState<string | null>(null)
  const [orphanPrompt, setOrphanPrompt] = useState<string | null>(null)
  const [threadsLoading, setThreadsLoading] = useState(true)
  const [transcriptLoading, setTranscriptLoading] = useState(() => Boolean(readActiveChatId()))
  const runsRef = useRef<Map<string, AbortController>>(new Map())
  const liveMessagesRef = useRef<Map<string, AssistantChatMessage[]>>(new Map())
  const saveTimersRef = useRef<Map<string, number>>(new Map())
  const deletedChatIdsRef = useRef<Set<string>>(new Set())
  const messagesRef = useRef<AssistantChatMessage[]>([])
  const chatIdRef = useRef<string | null>(chatId)
  const skipLoadRef = useRef(false)
  const persistedTranscriptRef = useRef('')
  const uidRef = useRef(uid)
  const assistantIdRef = useRef(assistantId)
  const transcriptOwnerIdRef = useRef(transcriptOwnerId)
  const authorNameRef = useRef(authorName)
  const chatMetaRef = useRef(new Map<string, { ownerId: string; assistantId: string | null }>())
  chatIdRef.current = chatId
  uidRef.current = uid
  assistantIdRef.current = assistantId
  transcriptOwnerIdRef.current = transcriptOwnerId
  authorNameRef.current = authorName
  messagesRef.current = messages
  const ownerIdFor = (id: string) => {
    const meta = chatMetaRef.current.get(id)
    if (meta?.ownerId) return meta.ownerId
    if (id === chatIdRef.current && transcriptOwnerIdRef.current) return transcriptOwnerIdRef.current
    return uidRef.current
  }
  const streaming = chatId != null && streamingChatIds.includes(chatId)
  const toolSteps = chatId ? toolStepsByChat[chatId] ?? [] : []

  const markStreaming = useCallback((id: string, active: boolean) => {
    setStreamingChatIds(prev => {
      const has = prev.includes(id)
      if (active && has) return prev
      if (!active && !has) return prev
      return active ? [...prev, id] : prev.filter(item => item !== id)
    })
  }, [])

  const scheduleSave = useCallback((id: string, next: AssistantChatMessage[]) => {
    if (!uidRef.current || next.length === 0 || deletedChatIdsRef.current.has(id)) return
    const pending = saveTimersRef.current.get(id)
    if (pending) window.clearTimeout(pending)
    const handle = window.setTimeout(() => {
      saveTimersRef.current.delete(id)
      const ownerId = ownerIdFor(id)
      if (!ownerId) return
      if (chatIdRef.current === id) persistedTranscriptRef.current = JSON.stringify(next)
      void saveOwnTranscript(ownerId, id, next).catch(() => {
        if (chatIdRef.current === id) {
          persistedTranscriptRef.current = ''
          setError('Could not save this chat')
        }
      })
    }, runsRef.current.has(id) ? 1500 : 400)
    saveTimersRef.current.set(id, handle)
  }, [])

  const publishRun = useCallback(
    (id: string, next: AssistantChatMessage[]) => {
      if (deletedChatIdsRef.current.has(id)) return
      liveMessagesRef.current.set(id, next)
      if (chatIdRef.current === id) setMessages(next)
      scheduleSave(id, next)
    },
    [scheduleSave],
  )

  const flushSave = useCallback(async (id: string, next: AssistantChatMessage[]) => {
    const pending = saveTimersRef.current.get(id)
    if (pending) window.clearTimeout(pending)
    saveTimersRef.current.delete(id)
    const ownerId = ownerIdFor(id)
    if (!ownerId || next.length === 0 || deletedChatIdsRef.current.has(id)) return
    if (chatIdRef.current === id) persistedTranscriptRef.current = JSON.stringify(next)
    await saveOwnTranscript(ownerId, id, next)
  }, [])

  const cancel = useCallback(() => {
    const id = chatIdRef.current
    if (!id) return
    runsRef.current.get(id)?.abort()
  }, [])

  useEffect(() => {
    if (!uid || !subscriptionsEnabled) {
      setThreads([])
      setThreadsLoading(false)
      return
    }
    setThreadsLoading(true)
    return subscribeOwnChats(uid, next => {
      setThreads(next.filter(chat => !deletedChatIdsRef.current.has(chat.id)))
      setThreadsLoading(false)
    })
  }, [uid, subscriptionsEnabled])

  useEffect(() => {
    if (!chatId || !subscriptionsEnabled) return
    return subscribeChatMeta(chatId, meta => {
      if (meta) chatMetaRef.current.set(chatId, meta)
      else chatMetaRef.current.delete(chatId)
    })
  }, [chatId, subscriptionsEnabled])

  useEffect(() => {
    if (!uid || !chatId || !subscriptionsEnabled) {
      setTranscriptLoading(false)
      return
    }
    const skipInitial = skipLoadRef.current
    skipLoadRef.current = false
    if (!skipInitial) {
      setTranscriptLoading(true)
      setMessages([])
    } else {
      setTranscriptLoading(false)
    }
    return subscribeOwnTranscript(
      chatId,
      loaded => {
        setTranscriptLoading(false)
        const local = liveMessagesRef.current.get(chatId) ?? []
        if (loaded.length === 0 && local.length > 0) return
        if (runsRef.current.has(chatId)) {
          const merged = mergeTranscriptMessages(loaded, local)
          liveMessagesRef.current.set(chatId, merged)
          if (chatIdRef.current === chatId) {
            messagesRef.current = merged
            setMessages(merged)
          }
          return
        }
        const serialized = JSON.stringify(loaded)
        if (serialized === persistedTranscriptRef.current) return
        persistedTranscriptRef.current = serialized
        messagesRef.current = loaded
        liveMessagesRef.current.set(chatId, loaded)
        if (chatIdRef.current === chatId) setMessages(loaded)
      },
      () => {
        setTranscriptLoading(false)
        setError('Could not open that chat')
      },
    )
  }, [uid, chatId, subscriptionsEnabled])

  const send = useCallback(
    async (content: string) => {
      const trimmed = content.trim()
      if (!trimmed) return
      if (chatIdRef.current && runsRef.current.has(chatIdRef.current)) return

      setError(null)
      const ownerId = uidRef.current
      if (!ownerId) {
        setError('Sign in to save chats')
        return
      }
      if (!chatIdRef.current) {
        try {
          const created = await createOwnChat(ownerId, trimmed)
          skipLoadRef.current = true
          chatIdRef.current = created
          writeActiveChatId(created)
          setChatId(created)
        } catch {
          setOrphanPrompt(trimmed)
          setError('Could not start a chat')
          return
        }
      }
      setOrphanPrompt(null)
      const runChatId = chatIdRef.current
      if (!runChatId) return
      const prior = liveMessagesRef.current.get(runChatId) ?? messagesRef.current
      const sharedAssistantId =
        chatMetaRef.current.get(runChatId)?.assistantId || assistantIdRef.current || null
      const userMessage: AssistantChatMessage = {
        id: `u-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
        role: 'user',
        content: trimmed,
        ...(sharedAssistantId
          ? {
              authorId: ownerId,
              authorName: authorNameRef.current?.trim() || 'Admin',
            }
          : {}),
      }
      const assistantId = `a-${Date.now()}`
      const controller = new AbortController()
      runsRef.current.set(runChatId, controller)
      setToolStepsByChat(prev => ({ ...prev, [runChatId]: [] }))
      markStreaming(runChatId, true)
      publishRun(runChatId, [
        ...prior,
        userMessage,
        { id: assistantId, role: 'assistant', content: '' },
      ])

      const history = [...prior, userMessage].map(m => ({
        role: m.role,
        content: m.content,
      }))

      try {
        const token = await getAuthToken()
        const response = await fetch(`${apiBaseUrl}/portal/admin/assistant/chat`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
          body: JSON.stringify({
            messages: history,
            currentPath,
            effort,
            model: model || undefined,
            toolPrefs: toolPrefs
              ? {
                  graphing: Boolean(toolPrefs.graphing),
                  chartTypes: toolPrefs.chartTypes ?? [],
                  maxRecords: toolPrefs.maxRecords,
                  rangePreset: toolPrefs.rangePreset,
                  rangeStart: toolPrefs.rangeStart,
                  rangeEnd: toolPrefs.rangeEnd,
                }
              : undefined,
            workspace: workspace || undefined,
            assistantId: sharedAssistantId || undefined,
          }),
          signal: controller.signal,
        })

        if (!response.ok || !response.body) {
          const text = await response.text().catch(() => '')
          throw new Error(text || `Assistant request failed (${response.status})`)
        }

        const reader = response.body.getReader()
        const decoder = new TextDecoder()
        let buffer = ''
        let assembled = ''
        let proposedActions: ProposedAction[] = []
        let graybookCards: GraybookCardData[] = []
        let charts: AssistantChartData[] = []
        let imageCards: AssistantImageCardData[] = []
        let tables: AssistantTableData[] = []
        let previews: AssistantPreviewData[] = []
        let suggestions: AssistantSuggestion[] = []
        let scanNotes: AssistantScanNote[] = []

        const isAction = (value: unknown): value is ProposedAction =>
          Boolean(
            value &&
              typeof value === 'object' &&
              typeof (value as ProposedAction).type === 'string' &&
              (value as ProposedAction).payload &&
              typeof (value as ProposedAction).payload === 'object',
          )

        const isGraybookCard = (value: unknown): value is GraybookCardData =>
          Boolean(
            value &&
              typeof value === 'object' &&
              (value as GraybookCardData).graybookCard === true &&
              Array.isArray((value as GraybookCardData).reasoning),
          )

        const isAssistantChart = (value: unknown): value is AssistantChartData =>
          Boolean(
            value &&
              typeof value === 'object' &&
              ((value as AssistantChartData).assistantChart === true ||
                (value as AssistantChartData).needsChartType === true) &&
              typeof (value as AssistantChartData).title === 'string',
          )

        const isImageCard = (value: unknown): value is AssistantImageCardData =>
          Boolean(
            value &&
              typeof value === 'object' &&
              (value as AssistantImageCardData).imageCard === true &&
              typeof (value as AssistantImageCardData).title === 'string' &&
              Array.isArray((value as AssistantImageCardData).items),
          )

        const applyActions = (next: ProposedAction[]) => {
          const cleaned = next.filter(isAction)
          if (!cleaned.length) return
          proposedActions = cleaned
          publishRun(
            runChatId,
            (liveMessagesRef.current.get(runChatId) ?? []).map(m =>
              m.id === assistantId ? { ...m, proposedActions: cleaned } : m,
            ),
          )
        }

        const applyGraybook = (next: GraybookCardData[]) => {
          const cleaned = next.filter(isGraybookCard)
          if (!cleaned.length) return
          graybookCards = cleaned
          publishRun(
            runChatId,
            (liveMessagesRef.current.get(runChatId) ?? []).map(m =>
              m.id === assistantId ? { ...m, graybookCards: cleaned } : m,
            ),
          )
        }

        const applyCharts = (next: AssistantChartData[]) => {
          const cleaned = next.filter(isAssistantChart)
          if (!cleaned.length) return
          charts = cleaned
          publishRun(
            runChatId,
            (liveMessagesRef.current.get(runChatId) ?? []).map(m =>
              m.id === assistantId ? { ...m, charts: cleaned } : m,
            ),
          )
        }

        const isTable = (value: unknown): value is AssistantTableData =>
          Boolean(
            value &&
              typeof value === 'object' &&
              (value as AssistantTableData).assistantTable === true &&
              typeof (value as AssistantTableData).title === 'string' &&
              Array.isArray((value as AssistantTableData).columns) &&
              Array.isArray((value as AssistantTableData).rows),
          )

        const isPreview = (value: unknown): value is AssistantPreviewData =>
          Boolean(
            value &&
              typeof value === 'object' &&
              (value as AssistantPreviewData).assistantPreview === true &&
              typeof (value as AssistantPreviewData).title === 'string' &&
              typeof (value as AssistantPreviewData).path === 'string',
          )

        const isSuggestion = (value: unknown): value is AssistantSuggestion =>
          Boolean(
            value &&
              typeof value === 'object' &&
              typeof (value as AssistantSuggestion).label === 'string' &&
              typeof (value as AssistantSuggestion).prompt === 'string',
          )

        const applyImages = (next: AssistantImageCardData[]) => {
          const cleaned = next.filter(isImageCard)
          if (!cleaned.length) return
          imageCards = cleaned
          publishRun(
            runChatId,
            (liveMessagesRef.current.get(runChatId) ?? []).map(m =>
              m.id === assistantId ? { ...m, imageCards: cleaned } : m,
            ),
          )
        }

        const applyTables = (next: AssistantTableData[]) => {
          const cleaned = next.filter(isTable)
          if (!cleaned.length) return
          tables = cleaned
          publishRun(
            runChatId,
            (liveMessagesRef.current.get(runChatId) ?? []).map(m =>
              m.id === assistantId ? { ...m, tables: cleaned } : m,
            ),
          )
        }

        const applyPreviews = (next: AssistantPreviewData[]) => {
          const cleaned = next.filter(isPreview)
          if (!cleaned.length) return
          previews = cleaned
          publishRun(
            runChatId,
            (liveMessagesRef.current.get(runChatId) ?? []).map(m =>
              m.id === assistantId ? { ...m, previews: cleaned } : m,
            ),
          )
        }

        const applySuggestions = (next: AssistantSuggestion[]) => {
          const cleaned = next.filter(isSuggestion).slice(0, 6)
          if (!cleaned.length) return
          suggestions = cleaned
          publishRun(
            runChatId,
            (liveMessagesRef.current.get(runChatId) ?? []).map(m =>
              m.id === assistantId ? { ...m, suggestions: cleaned } : m,
            ),
          )
        }

        const handleSsePart = (part: string) => {
          const lines = part.split('\n')
          let event = 'message'
          let dataLine = ''
          for (const line of lines) {
            if (line.startsWith('event:')) event = line.slice(6).trim()
            if (line.startsWith('data:')) dataLine += line.slice(5).trim()
          }
          if (!dataLine) return
          let data: Record<string, unknown>
          try {
            data = JSON.parse(dataLine) as Record<string, unknown>
          } catch {
            return
          }

          if (
            event === 'tool' &&
            typeof data.id === 'string' &&
            typeof data.name === 'string' &&
            (data.phase === 'start' || data.phase === 'done')
          ) {
            const step: AssistantToolStep = {
              id: data.id,
              name: data.name,
              phase: data.phase,
            }
            setToolStepsByChat(prev => {
              const list = prev[runChatId] ?? []
              const index = list.findIndex(item => item.id === step.id)
              const steps =
                index === -1
                  ? [...list, step]
                  : list.map((item, itemIndex) => (itemIndex === index ? step : item))
              return { ...prev, [runChatId]: steps }
            })
          }
          if (event === 'token' && typeof data.text === 'string') {
            assembled += data.text
            publishRun(
              runChatId,
              (liveMessagesRef.current.get(runChatId) ?? []).map(m =>
                m.id === assistantId ? { ...m, content: assembled } : m,
              ),
            )
          }
          if (event === 'actions' && Array.isArray(data.proposedActions)) {
            applyActions(data.proposedActions as ProposedAction[])
          }
          if (event === 'graybook' && Array.isArray(data.cards)) {
            applyGraybook(data.cards as GraybookCardData[])
          }
          if (event === 'charts' && Array.isArray(data.charts)) {
            applyCharts(data.charts as AssistantChartData[])
          }
          if (event === 'images' && Array.isArray(data.cards)) {
            applyImages(data.cards as AssistantImageCardData[])
          }
          if (event === 'tables' && Array.isArray(data.tables)) {
            applyTables(data.tables as AssistantTableData[])
          }
          if (event === 'previews' && Array.isArray(data.previews)) {
            applyPreviews(data.previews as AssistantPreviewData[])
          }
          if (event === 'suggestions' && Array.isArray(data.suggestions)) {
            applySuggestions(data.suggestions as AssistantSuggestion[])
          }
          if (event === 'scans' && Array.isArray(data.notes)) {
            scanNotes = (data.notes as AssistantScanNote[]).filter(
              note =>
                note &&
                typeof note.maxRecords === 'number' &&
                typeof note.startDate === 'string' &&
                typeof note.endDate === 'string',
            )
          }
          if (event === 'done') {
            const finalText =
              typeof data.text === 'string' && data.text.length > assembled.length
                ? data.text
                : assembled
            publishRun(
              runChatId,
              (liveMessagesRef.current.get(runChatId) ?? []).map(m =>
                m.id === assistantId
                  ? {
                      ...m,
                      content: finalText,
                      proposedActions: proposedActions.length ? proposedActions : undefined,
                      graybookCards: graybookCards.length ? graybookCards : undefined,
                      charts: charts.length ? charts : undefined,
                      imageCards: imageCards.length ? imageCards : undefined,
                      tables: tables.length ? tables : undefined,
                      previews: previews.length ? previews : undefined,
                      suggestions: suggestions.length ? suggestions : undefined,
                      scanNotes: scanNotes.length ? scanNotes : m.scanNotes,
                    }
                  : m,
              ),
            )
          }
          if (event === 'error') {
            throw new Error(typeof data.message === 'string' ? data.message : 'Assistant error')
          }
        }

        while (true) {
          const { done, value } = await reader.read()
          if (done) break
          buffer += decoder.decode(value, { stream: true })
          const parts = buffer.split('\n\n')
          buffer = parts.pop() || ''
          for (const part of parts) {
            handleSsePart(part)
          }
        }
        buffer += decoder.decode()
        if (buffer.trim()) {
          handleSsePart(buffer)
        }
      } catch (err) {
        if ((err as Error).name === 'AbortError') {
          publishRun(
            runChatId,
            (liveMessagesRef.current.get(runChatId) ?? []).map(m =>
              m.id === assistantId
                ? {
                    ...m,
                    content: m.content.trim()
                      ? `${m.content.trim()}\n\n_(Stopped)_`
                      : '_(Stopped)_',
                  }
                : m,
            ),
          )
          return
        }
        const message = err instanceof Error ? err.message : 'Assistant failed'
        if (chatIdRef.current === runChatId) setError(message)
        publishRun(
          runChatId,
          (liveMessagesRef.current.get(runChatId) ?? []).map(m =>
            m.id === assistantId
              ? {
                  ...m,
                  failed: true,
                  content: m.content.trim()
                    ? m.content
                    : 'Sorry — I could not complete that request.',
                }
              : m,
          ),
        )
      } finally {
        const finalMessages = liveMessagesRef.current.get(runChatId) ?? []
        markStreaming(runChatId, false)
        try {
          await flushSave(runChatId, finalMessages)
        } catch {
          if (chatIdRef.current === runChatId) setError('Could not save this chat')
        }
        if (runsRef.current.get(runChatId) === controller) runsRef.current.delete(runChatId)
        if (chatIdRef.current !== runChatId) liveMessagesRef.current.delete(runChatId)
      }
    },
    [currentPath, effort, model, flushSave, markStreaming, publishRun, toolPrefs, workspace],
  )

  const retry = useCallback(() => {
    if (chatIdRef.current && runsRef.current.has(chatIdRef.current)) return
    const id = chatIdRef.current
    const current = (id ? liveMessagesRef.current.get(id) : undefined) ?? messagesRef.current
    const tail = current[current.length - 1]
    if (tail?.role === 'assistant' && tail.failed) {
      let lastUser = -1
      for (let index = current.length - 1; index >= 0; index -= 1) {
        if (current[index].role === 'user') {
          lastUser = index
          break
        }
      }
      if (lastUser < 0) return
      const question = current[lastUser].content.trim()
      if (!question) return
      const base = current.slice(0, lastUser)
      messagesRef.current = base
      if (id) liveMessagesRef.current.set(id, base)
      setMessages(base)
      setError(null)
      void send(question)
      return
    }
    const prompt = orphanPrompt?.trim()
    if (!prompt) return
    setError(null)
    void send(prompt)
  }, [orphanPrompt, send])

  const newChat = useCallback(() => {
    skipLoadRef.current = false
    chatIdRef.current = null
    messagesRef.current = []
    writeActiveChatId(null)
    setChatId(null)
    setMessages([])
    setError(null)
    setOrphanPrompt(null)
  }, [])

  const openChat = useCallback((id: string) => {
    if (id === chatIdRef.current) return
    setError(null)
    setOrphanPrompt(null)
    chatIdRef.current = id
    writeActiveChatId(id)
    const live = liveMessagesRef.current.get(id)
    if (runsRef.current.has(id) && live) {
      skipLoadRef.current = true
      persistedTranscriptRef.current = JSON.stringify(live)
      messagesRef.current = live
      setMessages(live)
      setTranscriptLoading(false)
    } else {
      skipLoadRef.current = false
      messagesRef.current = []
      setMessages([])
    }
    setChatId(id)
  }, [])

  const renameChat = useCallback((id: string, title: string) => {
    void renameOwnChat(id, title).catch(() => setError('Could not rename that chat'))
  }, [])

  const deleteChat = useCallback((id: string) => {
    const removed = threads.find(chat => chat.id === id)
    deletedChatIdsRef.current.add(id)
    setThreads(prev => prev.filter(chat => chat.id !== id))
    const pendingSave = saveTimersRef.current.get(id)
    if (pendingSave) window.clearTimeout(pendingSave)
    saveTimersRef.current.delete(id)
    runsRef.current.get(id)?.abort()
    liveMessagesRef.current.delete(id)
    if (id === chatIdRef.current) {
      skipLoadRef.current = false
      chatIdRef.current = null
      messagesRef.current = []
      writeActiveChatId(null)
      setChatId(null)
      setMessages([])
    }
    void deleteOwnChats([id]).catch(() => {
      deletedChatIdsRef.current.delete(id)
      if (removed) {
        setThreads(prev =>
          sortChatsByRecent(prev.some(chat => chat.id === id) ? prev : [...prev, removed]),
        )
      }
      setError('Could not delete that chat')
    })
  }, [threads])

  const clear = useCallback(() => {
    for (const controller of runsRef.current.values()) controller.abort()
    const ids = threads.map(thread => thread.id)
    for (const id of ids) deletedChatIdsRef.current.add(id)
    setMessages([])
    setError(null)
    setOrphanPrompt(null)
    setStreamingChatIds([])
    chatIdRef.current = null
    writeActiveChatId(null)
    setChatId(null)
    if (ids.length) {
      void deleteOwnChats(ids).catch(() => setError('Could not clear chat history'))
    }
  }, [threads])

  const dismissError = useCallback(() => setError(null), [])

  return {
    messages,
    threads,
    chatId,
    streaming,
    streamingChatIds,
    toolSteps,
    threadsLoading,
    transcriptLoading,
    error,
    canRetry:
      !streaming &&
      (messages[messages.length - 1]?.failed === true || Boolean(orphanPrompt?.trim())),
    send,
    retry,
    cancel,
    clear,
    newChat,
    openChat,
    renameChat,
    deleteChat,
    dismissError,
  }
}
